import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import AdminLayout from '../../layouts/AdminLayout'
import {
  MdGroup,
  MdGroups2,
  MdGroups3,
  MdOutlineGroup,
  MdOutlineAnnouncement,
  MdOutlineMiscellaneousServices,
  MdArrowForward,
  MdDashboard,
  MdTrendingUp,
  MdAccessTime,
  MdCalendarToday,
  MdDragIndicator,
  MdRefresh,
  MdPlayArrow,
  MdPause,
  MdRestore,
  MdPieChart,
  MdShowChart,
  MdBarChart,
  MdFlashOn,
  MdCampaign,
  MdDns,
  MdSpeed,
  MdCheckCircle,
  MdChurch
} from 'react-icons/md'
import { FaPrayingHands, FaHeart } from 'react-icons/fa'
import { captialise_first_letter, currency, tidy_date } from '../../utils/helper'
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, registerables } from "chart.js"
import { Doughnut, Bar, Line } from "react-chartjs-2"

import { fetch_all_events } from '../../api/eventAPI'
import { fetch_all_announcements_admin } from '../../api/announcementAPI'
import { get_all_prayer_lists } from '../../api/prayerAPI'
import { fetch_all_services_admin } from '../../api/servicesAPI'

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, ...registerables)

const DEFAULT_WIDGET_ORDER = [
  'overview_stats',
  'upcoming_events',
  'recent_announcements',
  'prayer_support',
  'church_services',
  'system_health',
  'device_sessions',
  'user_distribution',
  'version_usage',
  'weekly_usage',
  'monthly_usage',
  'missions_usage',
  'aol_usage',
  'quick_actions'
]

const WIDGET_CONFIG = {
  overview_stats: { title: "Key Metrics", badge: "Full Width (1x5)", icon: <MdTrendingUp size={18} className="text-blue-500" />, colSpan: "col-span-12" },
  upcoming_events: { title: "Upcoming Events Schedule", badge: "2x2 Layout", icon: <MdCalendarToday size={18} className="text-amber-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" },
  recent_announcements: { title: "Recent Announcements Feed", badge: "2x2 Layout", icon: <MdCampaign size={18} className="text-purple-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" },
  prayer_support: { title: "Prayer Support Requests", badge: "2x2 Layout", icon: <FaPrayingHands size={16} className="text-rose-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" },
  church_services: { title: "Church Services Schedule", badge: "2x2 Layout", icon: <MdChurch size={18} className="text-emerald-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" },
  system_health: { title: "System Status & API Latency", badge: "2x2 Layout", icon: <MdDns size={18} className="text-cyan-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" },
  device_sessions: { title: "Device Sessions", badge: "2x2 Layout", icon: <MdPieChart size={18} className="text-blue-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" },
  user_distribution: { title: "User Distribution", badge: "2x2 Layout", icon: <MdPieChart size={18} className="text-emerald-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" },
  version_usage: { title: "App Version Usage", badge: "2x2 Layout", icon: <MdPieChart size={18} className="text-purple-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" },
  weekly_usage: { title: "Weekly Usage", badge: "2x2 Layout", icon: <MdShowChart size={18} className="text-indigo-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" },
  monthly_usage: { title: "Monthly Usage", badge: "2x2 Layout", icon: <MdBarChart size={18} className="text-amber-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" },
  missions_usage: { title: "Missions Trend", badge: "2x2 Layout", icon: <MdBarChart size={18} className="text-rose-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" },
  aol_usage: { title: "AOL Completions", badge: "2x2 Layout", icon: <MdBarChart size={18} className="text-cyan-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" },
  quick_actions: { title: "Quick Actions", badge: "2x2 Layout", icon: <MdFlashOn size={18} className="text-amber-500" />, colSpan: "col-span-12 md:col-span-6 xl:col-span-6" }
}

export default function AdminHomePage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  const user_data = useFetchUserData()

  const [userAnalyticData, setUserAnalyticData] = useState({})
  const [versionAnalyticData, setVersionAnalyticData] = useState({})
  const [eventsList, setEventsList] = useState([])
  const [announcementsList, setAnnouncementsList] = useState([])
  const [prayerLists, setPrayerLists] = useState([])
  const [servicesList, setServicesList] = useState([])
  const [apiLatency, setApiLatency] = useState(null)

  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(null)

  // Real-time monitoring state
  const [isRealTime, setIsRealTime] = useState(() => {
    const saved = localStorage.getItem('bmc_admin_realtime_monitoring')
    return saved !== null ? JSON.parse(saved) : false
  })
  const [refreshInterval, setRefreshInterval] = useState(() => {
    const saved = localStorage.getItem('bmc_admin_refresh_interval')
    return saved ? parseInt(saved, 10) : 10
  })

  // Drag and Drop widget state
  const [widgetOrder, setWidgetOrder] = useState(() => {
    const saved = localStorage.getItem('bmc_admin_homepage_widgets')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        const merged = [...parsed, ...DEFAULT_WIDGET_ORDER.filter(w => !parsed.includes(w))]
        return merged
      } catch (e) {
        return DEFAULT_WIDGET_ORDER
      }
    }
    return DEFAULT_WIDGET_ORDER
  })

  const [draggedWidgetKey, setDraggedWidgetKey] = useState(null)
  const [dragOverWidgetKey, setDragOverWidgetKey] = useState(null)

  const pollingRef = useRef(null)

  const datetime = () => {
    let dateObj = new Date()
    let hours = dateObj.getHours()
    let minutes = dateObj.getMinutes()
    let seconds = dateObj.getSeconds()
    let day = dateObj.getDay()
    let date = dateObj.getDate()
    let month = dateObj.getMonth()
    let year = dateObj.getFullYear()
    const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
    const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
    return {
      date: `${date} ${MONTHS[month].slice(0, 3)}`,
      time: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
      day: DAYS[day],
      month: MONTHS[month],
      year
    }
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return "Good morning"
    if (hour < 17) return "Good afternoon"
    return "Good evening"
  }

  const SIZE = 28

  const fetchAllAnalytics = async (showLoading = false) => {
    if (showLoading) setIsRefreshing(true)
    const startTime = Date.now()
    try {
      await Promise.all([
        fetchUserAnalytics(),
        fetchVersionAnalytics(),
        fetchEventsData(),
        fetchAnnouncementsData(),
        fetchPrayerData(),
        fetchServicesData()
      ])
      const latency = Date.now() - startTime
      setApiLatency(latency)
      setLastUpdated(new Date().toLocaleTimeString())
    } catch (err) {
      console.error("Error fetching analytics:", err)
    } finally {
      if (showLoading) setTimeout(() => setIsRefreshing(false), 400)
    }
  }

  useEffect(() => {
    fetchAllAnalytics(true)
  }, [])

  // Handle Real-Time Polling
  useEffect(() => {
    localStorage.setItem('bmc_admin_realtime_monitoring', JSON.stringify(isRealTime))
    localStorage.setItem('bmc_admin_refresh_interval', refreshInterval.toString())

    if (pollingRef.current) clearInterval(pollingRef.current)

    if (isRealTime) {
      pollingRef.current = setInterval(() => {
        fetchAllAnalytics(false)
      }, refreshInterval * 1000)
    }

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [isRealTime, refreshInterval])

  const fetchUserAnalytics = async () => {
    let endpoint = config.endpoint + "/analytics/data/users"
    let fetchResponse = await fetch(endpoint, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
    let data = await fetchResponse.json()
    if (data.status === 200) setUserAnalyticData(data.data)
  }

  const fetchVersionAnalytics = async () => {
    let endpoint = config.endpoint + "/version/analytics"
    let fetchResponse = await fetch(endpoint, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
    let data = await fetchResponse.json()
    if (data.status === 200) setVersionAnalyticData(data.data)
  }

  const fetchEventsData = async () => {
    const res = await fetch_all_events()
    if (res?.data && Array.isArray(res.data)) {
      setEventsList(res.data.slice(0, 5))
    }
  }

  const fetchAnnouncementsData = async () => {
    const res = await fetch_all_announcements_admin()
    if (res?.data && Array.isArray(res.data)) {
      setAnnouncementsList(res.data.slice(0, 5))
    }
  }

  const fetchPrayerData = async () => {
    const res = await get_all_prayer_lists()
    if (res?.data && Array.isArray(res.data)) {
      setPrayerLists(res.data.slice(0, 5))
    }
  }

  const fetchServicesData = async () => {
    const res = await fetch_all_services_admin()
    if (res?.data && Array.isArray(res.data)) {
      setServicesList(res.data.slice(0, 5))
    }
  }

  // Drag and drop handlers
  const handleDragStart = (e, key) => {
    setDraggedWidgetKey(key)
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", key)
  }

  const handleDragOver = (e, key) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    if (dragOverWidgetKey !== key) setDragOverWidgetKey(key)
  }

  const handleDrop = (e, targetKey) => {
    e.preventDefault()
    if (!draggedWidgetKey || draggedWidgetKey === targetKey) {
      setDraggedWidgetKey(null)
      setDragOverWidgetKey(null)
      return
    }

    const currentIndex = widgetOrder.indexOf(draggedWidgetKey)
    const targetIndex = widgetOrder.indexOf(targetKey)

    if (currentIndex !== -1 && targetIndex !== -1) {
      const newOrder = [...widgetOrder]
      const [removed] = newOrder.splice(currentIndex, 1)
      newOrder.splice(targetIndex, 0, removed)
      setWidgetOrder(newOrder)
      localStorage.setItem('bmc_admin_homepage_widgets', JSON.stringify(newOrder))
    }

    setDraggedWidgetKey(null)
    setDragOverWidgetKey(null)
  }

  const handleResetLayout = () => {
    setWidgetOrder(DEFAULT_WIDGET_ORDER)
    localStorage.removeItem('bmc_admin_homepage_widgets')
  }

  const shortcutCards = [
    {
      title: "View Members",
      description: "Manage church members and users",
      icon: <MdOutlineGroup size={20} className="text-blue-600" />,
      link: "/admin/users",
    },
    {
      title: "Create Announcement",
      description: "Send announcements to members",
      icon: <MdOutlineAnnouncement size={20} className="text-purple-600" />,
      link: "/admin/announcements",
    },
    {
      title: "Manage Services",
      description: "Schedule and organize services",
      icon: <MdOutlineMiscellaneousServices size={20} className="text-emerald-600" />,
      link: "/admin/services",
    },
    {
      title: "View Events",
      description: "Manage church events and activities",
      icon: <MdCalendarToday size={20} className="text-amber-600" />,
      link: "/admin/events",
    },
    {
      title: "Prayer Requests",
      description: "View and manage prayer requests",
      icon: <MdAccessTime size={20} className="text-rose-600" />,
      link: "/admin/prayer",
    },
  ]

  // Render Widget Content Switcher
  const renderWidgetContent = (key) => {
    switch (key) {
      case 'overview_stats':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-sm p-5 border border-blue-100 hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Total Users</p>
                  <p className="text-3xl font-extrabold text-gray-900 mt-1">{currency(userAnalyticData?.total_users || 0)}</p>
                </div>
                <div className="bg-blue-500/10 p-3 rounded-xl">
                  <MdGroup size={SIZE} className="text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl shadow-sm p-5 border border-emerald-100 hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Registered</p>
                  <p className="text-3xl font-extrabold text-gray-900 mt-1">{currency(userAnalyticData?.members || 0)}</p>
                </div>
                <div className="bg-emerald-500/10 p-3 rounded-xl">
                  <MdGroups2 size={SIZE} className="text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl shadow-sm p-5 border border-purple-100 hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Guests</p>
                  <p className="text-3xl font-extrabold text-gray-900 mt-1">{currency(userAnalyticData?.guest_users || 0)}</p>
                </div>
                <div className="bg-purple-500/10 p-3 rounded-xl">
                  <MdGroups3 size={SIZE} className="text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl shadow-sm p-5 border border-amber-100 hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Missions</p>
                  <p className="text-3xl font-extrabold text-gray-900 mt-1">{currency(userAnalyticData?.missions_count || 0)}</p>
                </div>
                <div className="bg-amber-500/10 p-3 rounded-xl">
                  <MdOutlineMiscellaneousServices size={SIZE} className="text-amber-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-white rounded-xl shadow-sm p-5 border border-red-100 hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-red-600 uppercase tracking-wider">Acts of Love</p>
                  <p className="text-3xl font-extrabold text-gray-900 mt-1">
                    {currency(Object.values(userAnalyticData?.aol_by_act || {})?.reduce((a, b) => a + b, 0))}
                  </p>
                </div>
                <div className="bg-red-500/10 p-3 rounded-xl">
                  <FaHeart size={24} className="text-red-500" />
                </div>
              </div>
            </div>
          </div>
        )

      case 'upcoming_events':
        return (
          <div className="h-64 overflow-y-auto space-y-2.5 pr-1">
            {eventsList.length > 0 ? (
              eventsList.map((item, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between hover:bg-white hover:shadow-sm transition-all">
                  <div className="flex items-center space-x-3">
                    <div className="bg-amber-100 p-2.5 rounded-lg text-amber-600">
                      <MdCalendarToday size={18} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-gray-900 leading-snug">{item.name || item.title || "Church Event"}</h4>
                      <p className="text-[11px] text-gray-500 mt-0.5">{item.date ? tidy_date(item.date) : "Upcoming"} {item.location ? `• ${item.location}` : ''}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-md border border-amber-200 uppercase">
                    Active
                  </span>
                </div>
              ))
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 text-xs font-medium">
                No upcoming events listed
              </div>
            )}
          </div>
        )

      case 'recent_announcements':
        return (
          <div className="h-64 overflow-y-auto space-y-2.5 pr-1">
            {announcementsList.length > 0 ? (
              announcementsList.map((item, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between hover:bg-white hover:shadow-sm transition-all">
                  <div className="flex items-center space-x-3">
                    <div className="bg-purple-100 p-2.5 rounded-lg text-purple-600">
                      <MdCampaign size={18} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-gray-900 leading-snug line-clamp-1">{item.title || item.announcement_title || "Announcement"}</h4>
                      <p className="text-[11px] text-gray-500 mt-0.5">{item.created_at ? tidy_date(item.created_at) : 'Published'}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-semibold text-purple-700 bg-purple-50 px-2 py-1 rounded-md border border-purple-200 uppercase">
                    Announced
                  </span>
                </div>
              ))
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 text-xs font-medium">
                No recent announcements found
              </div>
            )}
          </div>
        )

      case 'prayer_support':
        return (
          <div className="h-64 overflow-y-auto space-y-2.5 pr-1">
            {prayerLists.length > 0 ? (
              prayerLists.map((item, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between hover:bg-white hover:shadow-sm transition-all">
                  <div className="flex items-center space-x-3">
                    <div className="bg-rose-100 p-2.5 rounded-lg text-rose-600">
                      <FaPrayingHands size={16} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-gray-900 leading-snug">{item.title || item.name || "Prayer List Item"}</h4>
                      <p className="text-[11px] text-gray-500 mt-0.5">{item.created_at ? tidy_date(item.created_at) : 'Active Request'}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-semibold text-rose-700 bg-rose-50 px-2 py-1 rounded-md border border-rose-200 uppercase">
                    Praying
                  </span>
                </div>
              ))
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 text-xs font-medium">
                No active prayer requests
              </div>
            )}
          </div>
        )

      case 'church_services':
        return (
          <div className="h-64 overflow-y-auto space-y-2.5 pr-1">
            {servicesList.length > 0 ? (
              servicesList.map((item, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between hover:bg-white hover:shadow-sm transition-all">
                  <div className="flex items-center space-x-3">
                    <div className="bg-emerald-100 p-2.5 rounded-lg text-emerald-600">
                      <MdChurch size={18} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-gray-900 leading-snug">{item.name || item.service_name || "Worship Service"}</h4>
                      <p className="text-[11px] text-gray-500 mt-0.5">{item.time || item.venue || "Weekly Service"}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md border border-emerald-200 uppercase">
                    Scheduled
                  </span>
                </div>
              ))
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 text-xs font-medium">
                No service schedules loaded
              </div>
            )}
          </div>
        )

      case 'system_health':
        return (
          <div className="h-64 flex flex-col justify-between p-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3.5 bg-emerald-50 rounded-xl border border-emerald-200 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold text-emerald-800 uppercase">Backend API</p>
                  <p className="text-sm font-extrabold text-emerald-600 mt-0.5">ONLINE</p>
                </div>
                <MdCheckCircle size={22} className="text-emerald-500" />
              </div>

              <div className="p-3.5 bg-blue-50 rounded-xl border border-blue-200 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold text-blue-800 uppercase">API Latency</p>
                  <p className="text-sm font-extrabold text-blue-600 mt-0.5">{apiLatency !== null ? `${apiLatency} ms` : 'Measuring...'}</p>
                </div>
                <MdSpeed size={22} className="text-blue-500" />
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2 mt-2">
              <div className="flex justify-between items-center text-xs text-gray-600 font-medium">
                <span>Real-Time Engine:</span>
                <span className={`font-bold ${isRealTime ? 'text-emerald-600' : 'text-gray-500'}`}>
                  {isRealTime ? `Active (${refreshInterval}s)` : 'Disabled'}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs text-gray-600 font-medium">
                <span>API Endpoint:</span>
                <span className="font-mono text-[11px] text-gray-700">{config.endpoint}</span>
              </div>
              <div className="flex justify-between items-center text-xs text-gray-600 font-medium">
                <span>Active Widgets:</span>
                <span className="font-bold text-gray-800">{widgetOrder.length} Loaded</span>
              </div>
            </div>

            <div className="text-[11px] text-center text-gray-400 font-medium border-t pt-2">
              All backend endpoints responsive
            </div>
          </div>
        )

      case 'device_sessions':
        return (
          <div className="h-64">
            <Doughnut
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom' } }
              }}
              data={{
                labels: Object.keys(userAnalyticData?.users_by_device || {})?.map((device) => captialise_first_letter(device)),
                datasets: [
                  {
                    data: Object.values(userAnalyticData?.users_by_device || {}),
                    backgroundColor: ["#3B82F6", "#EF4444", "#F59E0B", "#10B981", "#06B6D4", "#8B5CF6"],
                    borderWidth: 0
                  },
                ],
              }}
            />
          </div>
        )

      case 'user_distribution':
        return (
          <div className="h-64">
            <Doughnut
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom' } }
              }}
              data={{
                labels: ["Members", "Guests"],
                datasets: [
                  {
                    data: [userAnalyticData?.members || 0, userAnalyticData?.guest_users || 0],
                    backgroundColor: ["#3B82F6", "#EF4444"],
                    borderWidth: 0
                  },
                ],
              }}
            />
          </div>
        )

      case 'version_usage':
        return (
          <div className="h-64 flex flex-col justify-between">
            <div className="flex-1">
              {versionAnalyticData?.version_distribution && versionAnalyticData.version_distribution.length > 0 ? (
                <Doughnut
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom' },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            const version = versionAnalyticData.version_distribution[context.dataIndex];
                            return `${version.label}: ${version.value} devices (${version.percentage}%)`;
                          }
                        }
                      }
                    }
                  }}
                  data={{
                    labels: versionAnalyticData?.version_distribution?.map((version) => version.label),
                    datasets: [
                      {
                        data: versionAnalyticData?.version_distribution?.map((version) => version.value),
                        backgroundColor: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#F97316", "#EC4899"],
                        borderWidth: 0
                      },
                    ],
                  }}
                />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-gray-500 text-sm">No version data available yet</p>
                    <p className="text-gray-400 text-xs mt-1">Data will appear once users log into mobile app</p>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-2 text-center text-xs text-gray-500 font-medium border-t pt-2">
              Total devices tracked: {versionAnalyticData?.total_users_with_versions || 0}
            </div>
          </div>
        )

      case 'weekly_usage':
        return (
          <div className="h-64">
            <Line
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true } }
              }}
              data={{
                labels: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                datasets: [
                  {
                    label: "Weekly Users",
                    data: Object.values(userAnalyticData?.daily_users || {}),
                    borderColor: "#3B82F6",
                    backgroundColor: "rgba(59, 130, 246, 0.1)",
                    fill: true,
                    tension: 0.4
                  },
                ],
              }}
            />
          </div>
        )

      case 'monthly_usage':
        return (
          <div className="h-64">
            <Bar
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true } }
              }}
              data={{
                labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
                datasets: [
                  {
                    label: "Monthly Users",
                    data: Object.values(userAnalyticData?.monthly_users || {}),
                    backgroundColor: "#3B82F6",
                    borderRadius: 6
                  },
                ],
              }}
            />
          </div>
        )

      case 'missions_usage':
        return (
          <div className="h-64">
            <Bar
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true } }
              }}
              data={{
                labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
                datasets: [
                  {
                    label: "Missions",
                    data: Object.values(userAnalyticData?.missions_per_month || {}),
                    backgroundColor: "#F59E0B",
                    borderRadius: 6
                  },
                ],
              }}
            />
          </div>
        )

      case 'aol_usage':
        return (
          <div className="h-64">
            <Bar
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true } }
              }}
              data={{
                labels: Object.keys(userAnalyticData?.aol_by_act || {}),
                datasets: [
                  {
                    label: 'Completions',
                    data: Object.values(userAnalyticData?.aol_by_act || {}),
                    backgroundColor: '#06B6D4',
                    borderRadius: 6
                  }
                ]
              }}
            />
          </div>
        )

      case 'quick_actions':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 h-64 overflow-y-auto pr-1">
            {shortcutCards.map((card, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl border border-gray-200 p-3.5 shadow-sm hover:shadow-md hover:border-blue-500 transition-all duration-200 cursor-pointer flex items-center justify-between group"
                onClick={() => navigate(card.link)}
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-lg bg-white shadow-sm border border-gray-100 group-hover:scale-105 transition-transform">
                    {card.icon}
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{card.title}</h4>
                    <p className="text-xs text-gray-500 leading-tight line-clamp-1">{card.description}</p>
                  </div>
                </div>
                <MdArrowForward size={18} className="text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
              </div>
            ))}
          </div>
        )

      default:
        return null
    }
  }

  return (
    <AdminLayout navSelected={"Dashboard"}>
      {/* Top Welcome Header & Real-Time Monitoring Toolbar */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
            {getGreeting()}, {user_data?.first_name || 'Admin'}!
          </h1>
          <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
            <span className="flex items-center gap-1.5 font-medium">
              <MdCalendarToday size={16} className="text-blue-500" />
              {datetime().day}, {datetime().date} {datetime().month} {datetime().year}
            </span>
            {lastUpdated && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full font-mono">
                Updated: {lastUpdated}
              </span>
            )}
          </div>
        </div>

        {/* Controls Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Real-Time Status Indicator Badge */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${
            isRealTime ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-600 border-gray-200'
          }`}>
            <span className={`w-2 h-2 rounded-full ${isRealTime ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`}></span>
            {isRealTime ? `LIVE (${refreshInterval}s)` : 'PAUSED'}
          </div>

          {/* Toggle Real-Time Switch */}
          <button
            onClick={() => setIsRealTime(!isRealTime)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all shadow-sm cursor-pointer ${
              isRealTime 
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
            }`}
          >
            {isRealTime ? <MdPause size={16} /> : <MdPlayArrow size={16} />}
            <span>{isRealTime ? 'Real-Time ON' : 'Enable Real-Time'}</span>
          </button>

          {/* Refresh Interval Selector */}
          {isRealTime && (
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(parseInt(e.target.value, 10))}
              className="bg-gray-50 border border-gray-300 text-gray-700 text-xs rounded-xl px-2.5 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none font-medium cursor-pointer"
            >
              <option value={5}>Every 5s</option>
              <option value={10}>Every 10s</option>
              <option value={30}>Every 30s</option>
              <option value={60}>Every 60s</option>
            </select>
          )}

          {/* Manual Refresh Button */}
          <button
            onClick={() => fetchAllAnalytics(true)}
            disabled={isRefreshing}
            className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl transition-all border border-blue-200 shadow-sm disabled:opacity-50 cursor-pointer"
            title="Refresh Data Now"
          >
            <MdRefresh size={18} className={isRefreshing ? 'animate-spin' : ''} />
          </button>

          {/* Reset Layout Button */}
          <button
            onClick={handleResetLayout}
            className="flex items-center gap-1 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-xl text-xs font-medium border border-gray-200 transition-all shadow-sm cursor-pointer"
            title="Reset Grid Layout"
          >
            <MdRestore size={16} />
            <span>Reset Grid</span>
          </button>
        </div>
      </div>

      {/* Grid Layout Container for Drag & Drop Widgets */}
      <div className="grid grid-cols-12 gap-6 w-full max-w-full">
        {widgetOrder.map((key) => {
          const configItem = WIDGET_CONFIG[key]
          if (!configItem) return null

          const isBeingDragged = draggedWidgetKey === key
          const isDragOver = dragOverWidgetKey === key

          return (
            <div
              key={key}
              className={`${configItem.colSpan} transition-all duration-200`}
              draggable
              onDragStart={(e) => handleDragStart(e, key)}
              onDragOver={(e) => handleDragOver(e, key)}
              onDrop={(e) => handleDrop(e, key)}
            >
              <div className={`bg-white rounded-2xl shadow-sm border transition-all duration-200 overflow-hidden ${
                isBeingDragged 
                  ? 'opacity-40 border-dashed border-blue-400 scale-[0.98]' 
                  : isDragOver 
                    ? 'border-2 border-blue-500 shadow-lg scale-[1.01]' 
                    : 'border-gray-200 hover:shadow-md'
              }`}>
                {/* Drag Handle & Header */}
                <div className="px-5 py-3.5 bg-gray-50/80 border-b border-gray-100 flex items-center justify-between cursor-grab active:cursor-grabbing group">
                  <div className="flex items-center space-x-2.5">
                    <MdDragIndicator size={20} className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                    {configItem.icon}
                    <h3 className="text-sm font-bold text-gray-800 tracking-tight">{configItem.title}</h3>
                  </div>

                  <span className="text-[11px] font-semibold text-gray-500 bg-gray-200/60 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                    {configItem.badge}
                  </span>
                </div>

                {/* Widget Main Body Content */}
                <div className="p-5">
                  {renderWidgetContent(key)}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </AdminLayout>
  )
}
